"use client"

import { useQuery } from "@tanstack/react-query"
import { RefreshCw } from "lucide-react"
import { parseAsString, useQueryState } from "nuqs"

import {
  getPopularCategoryClassName,
  PopularProductCard,
  productGridClass,
} from "@/components/home/popular-products"
import { fetchCategory, fetchTrending } from "@/lib/api"
import type { ICategory, ITrendingProduct } from "@/types"

export const POPULAR_PRODUCTS_LIMIT = 200

export function TrendingProductsFrame({
  trending,
  children,
}: {
  trending: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="min-h-screen bg-white">
      <section
        aria-labelledby="trending-products-title"
        className="bg-[#F2F3F5]"
      >
        <div className="mx-auto max-w-[1440px] px-4 py-10 sm:px-8 sm:py-14 lg:px-[72px]">
          <h1
            id="trending-products-title"
            className="text-[32px] leading-10 font-bold text-[#1D2129]"
          >
            Trending Products Right Now
          </h1>
          <div className="mt-8">{trending}</div>
        </div>
      </section>

      <section aria-labelledby="most-popular-products-title">
        <div className="mx-auto max-w-[1440px] px-4 py-10 sm:mt-[44px] sm:px-8 sm:py-14 lg:px-[72px]">
          <h2
            id="most-popular-products-title"
            className="text-[32px] leading-10 font-bold text-[#1D2129]"
          >
            Most Popular Products
          </h2>
          {children}
        </div>
      </section>
    </div>
  )
}

export default function DesignTrendingProductsPage({
  initialCategories,
  initialTrendingProducts,
  initialPopularProducts,
  initialCategoryId,
}: {
  initialCategories?: ICategory[]
  initialTrendingProducts?: ITrendingProduct[]
  initialPopularProducts?: ITrendingProduct[]
  initialCategoryId: string
}) {
  const [requestedCategoryId, setCategoryId] = useQueryState(
    "id",
    parseAsString.withDefault("").withOptions({ shallow: true })
  )
  const categoryQuery = useQuery({
    queryKey: ["home", "popular-product-categories"],
    queryFn: fetchCategory,
    initialData: initialCategories,
    staleTime: 60_000,
  })
  const categories = (categoryQuery.data ?? []).filter(
    (category) =>
      category.id !== "689325269cd2ea93971fbf73" && category.name !== "All"
  )
  const categoryId = categories.some(
    (category) => category.id === requestedCategoryId
  )
    ? requestedCategoryId
    : ""
  const filters = [{ id: "", name: "Trending" }, ...categories]
  const trendingQuery = useQuery({
    queryKey: ["trending-products", "trending"],
    queryFn: () => fetchTrending(1, "", 5),
    initialData: initialTrendingProducts,
    staleTime: 60_000,
  })
  const popularQuery = useQuery({
    queryKey: ["trending-products", "popular-products", categoryId],
    queryFn: () => fetchTrending(1, categoryId, POPULAR_PRODUCTS_LIMIT),
    initialData:
      categoryId === initialCategoryId ? initialPopularProducts : undefined,
    staleTime: 60_000,
  })

  return (
    <TrendingProductsFrame
      trending={
        <ProductResults
          products={trendingQuery.data}
          pending={trendingQuery.isPending}
          error={trendingQuery.isError}
          retry={() => trendingQuery.refetch()}
          skeletonCount={5}
        />
      }
    >
      <div
        role="group"
        aria-label="Popular product categories"
        className="mt-8 flex flex-wrap gap-2"
      >
        {filters.map((category) => (
          <button
            key={category.id}
            type="button"
            aria-pressed={categoryId === category.id}
            aria-controls="most-popular-products-grid"
            onClick={() => setCategoryId(category.id)}
            className={getPopularCategoryClassName(categoryId === category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>
      {categoryQuery.isError ? (
        <div
          role="alert"
          className="mt-3 flex flex-wrap items-center gap-3 text-[14px] text-[#4E5969]"
        >
          Categories could not be loaded.
          <button
            type="button"
            onClick={() => categoryQuery.refetch()}
            className="inline-flex items-center gap-1 text-[#0F9E4F]"
          >
            <RefreshCw aria-hidden="true" size={14} /> Retry
          </button>
        </div>
      ) : null}
      <div
        id="most-popular-products-grid"
        aria-busy={popularQuery.isPending}
        className="mt-8"
      >
        <ProductResults
          products={popularQuery.data}
          pending={popularQuery.isPending}
          error={popularQuery.isError}
          retry={() => popularQuery.refetch()}
          skeletonCount={10}
        />
      </div>
    </TrendingProductsFrame>
  )
}

export function TrendingProductsSkeleton({ count }: { count: number }) {
  return (
    <div
      role="status"
      aria-label="Loading products"
      className={productGridClass}
    >
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          aria-hidden="true"
          className="overflow-hidden rounded-[12px] bg-white motion-safe:animate-pulse"
        >
          <div className="aspect-square bg-white" />
          <div className="h-[160px] p-4">
            <div className="mb-1 h-4 w-20 rounded bg-[#F2F3F5]" />
            <div className="h-10 rounded bg-[#F2F3F5]" />
            <div className="mt-3 h-4 w-12 rounded bg-[#F2F3F5]" />
            <div className="mt-4 h-6 w-20 rounded bg-[#F2F3F5]" />
          </div>
        </div>
      ))}
    </div>
  )
}

function ProductResults({
  products,
  pending,
  error,
  retry,
  skeletonCount,
}: {
  products?: ITrendingProduct[]
  pending: boolean
  error: boolean
  retry: () => void
  skeletonCount: number
}) {
  if (pending) return <TrendingProductsSkeleton count={skeletonCount} />

  if (error) {
    return (
      <div
        role="alert"
        className="flex min-h-[180px] flex-col items-center justify-center gap-4 text-[14px] text-[#4E5969]"
      >
        Products could not be loaded.
        <button
          type="button"
          onClick={retry}
          className="inline-flex items-center gap-2 text-[#0F9E4F]"
        >
          <RefreshCw aria-hidden="true" size={16} /> Retry
        </button>
      </div>
    )
  }

  if (!products?.length) {
    return (
      <p
        role="status"
        className="flex min-h-[180px] items-center justify-center text-[14px] text-[#4E5969]"
      >
        No products in this category yet.
      </p>
    )
  }

  return (
    <div className={productGridClass}>
      {products.map((item) => (
        <PopularProductCard key={item.id} item={item} imageBackground="white" />
      ))}
    </div>
  )
}
